<template>
  <div ref="gridContainer" class="gallery-grid">
    <component
      :is="activeLayout"
      :images="images"
      :loaded-images="loadedImages"
      :image-aspect-ratios="imageAspectRatios"
      :columns="responsiveColumns"
      :max-columns="columns"
      :gap="gap"
      :container-width="containerWidth"
      :aspect-ratio-value="aspectRatioValue"
      :row-height="rowHeight"
      :appearance="itemAppearance"
      @item-click="$emit('item-click', $event)"
      @image-load="handleImageLoad"
    />
  </div>
</template>

<script>
import GridLayout from './layouts/GridLayout.vue'
import MasonryLayout from './layouts/MasonryLayout.vue'
import RowsLayout from './layouts/RowsLayout.vue'

const ASPECT_RATIOS = {
  square: '1 / 1',
  landscape: '4 / 3',
  portrait: '3 / 4'
}

const LAYOUTS = {
  grid: GridLayout,
  masonry: MasonryLayout,
  rows: RowsLayout
}

export default {
  name: 'GalleryGrid',

  components: { GridLayout, MasonryLayout, RowsLayout },

  props: {
    images: {
      type: Array,
      required: true
    },
    layout: {
      type: String,
      default: 'rows',
      validator: (v) => ['grid', 'masonry', 'rows'].includes(v)
    },
    columns: {
      type: Number,
      default: 3
    },
    gap: {
      type: Number,
      default: 15
    },
    aspectRatio: {
      type: String,
      default: 'square',
      validator: (v) => ['square', 'landscape', 'portrait'].includes(v)
    },
    clickAction: {
      type: String,
      default: 'none',
      validator: (v) => ['none', 'navigate', 'lightbox', 'emit'].includes(v)
    },
    rowHeight: {
      type: Number,
      default: 200
    },
    overlayStyle: {
      type: String,
      default: 'none',
      validator: (v) => ['none', 'bottom', 'full'].includes(v)
    },
    overlaySubtext: {
      type: String,
      default: ''
    },
    showOverlaySubtext: {
      type: Boolean,
      default: true
    },
    showSoldBadge: {
      type: Boolean,
      default: true
    },
    titleFontSize: {
      type: Number,
      default: 18
    },
    hoverEffect: {
      type: String,
      default: 'none',
      validator: (v) => ['none', 'zoom', 'darken', 'fade'].includes(v)
    },
    imageStyle: {
      type: String,
      default: 'none',
      validator: (v) => ['none', 'rounded', 'shadow', 'border'].includes(v)
    },
    showCaption: {
      type: Boolean,
      default: false
    },
    showPriceInOverlay: {
      type: Boolean,
      default: false
    },
    showPriceWhenSold: {
      type: Boolean,
      default: false
    },
    pinHover: {
      type: Boolean,
      default: false
    }
  },

  emits: ['item-click'],

  data() {
    return {
      loadedImages: {},
      imageAspectRatios: {},
      containerWidth: 0
    }
  },

  computed: {
    activeLayout() {
      return LAYOUTS[this.layout] || GridLayout
    },
    responsiveColumns() {
      const w = this.containerWidth
      if (w <= 0) return Math.min(2, this.columns)
      if (w <= 480) return 1
      if (w <= 768) return Math.min(2, this.columns)
      return this.columns
    },
    aspectRatioValue() {
      return ASPECT_RATIOS[this.aspectRatio] || ASPECT_RATIOS.square
    },
    itemAppearance() {
      return {
        overlayStyle: this.overlayStyle,
        overlaySubtext: this.overlaySubtext,
        showOverlaySubtext: this.showOverlaySubtext,
        showSoldBadge: this.showSoldBadge,
        titleFontSize: this.titleFontSize,
        hoverEffect: this.hoverEffect,
        imageStyle: this.imageStyle,
        showCaption: this.showCaption,
        showPriceInOverlay: this.showPriceInOverlay,
        showPriceWhenSold: this.showPriceWhenSold,
        pinnedHoverId: this.pinHover ? (this.images[0]?.id ?? null) : null,
        clickable: this.clickAction !== 'none'
      }
    }
  },

  watch: {
    images: {
      immediate: true,
      handler(newImages) {
        this.loadCachedAspectRatios(newImages)
      }
    }
  },

  mounted() {
    this._cacheFlushTimer = null
    this._pendingCache = {}
    this._observer = new ResizeObserver(entries => {
      requestAnimationFrame(() => {
        if (!entries.length) return
        const newWidth = entries[0].contentRect.width
        if (newWidth !== this.containerWidth) {
          this.containerWidth = newWidth
        }
      })
    })

    if (this.$refs.gridContainer) {
      this._observer.observe(this.$refs.gridContainer)
      this.containerWidth = this.$refs.gridContainer.offsetWidth
    }
  },

  beforeUnmount() {
    this._observer?.disconnect()
    if (this._cacheFlushTimer) {
      clearTimeout(this._cacheFlushTimer)
      this._flushAspectRatioCache()
    }
  },

  methods: {
    handleImageLoad({ id, aspectRatio }) {
      this.loadedImages[id] = true
      if (aspectRatio) {
        this.imageAspectRatios[id] = aspectRatio
        this.cacheAspectRatio(id, aspectRatio)
      }
    },
    loadCachedAspectRatios(images) {
      if (!images?.length) return
      const cached = this.getAspectRatioCache()
      for (const image of images) {
        if (image.aspectRatio) {
          this.imageAspectRatios[image.id] = image.aspectRatio
        } else if (cached[image.id]) {
          this.imageAspectRatios[image.id] = cached[image.id]
        }
      }
    },
    getAspectRatioCache() {
      try {
        const cache = localStorage.getItem('gallery-aspect-ratios')
        return cache ? JSON.parse(cache) : {}
      } catch {
        return {}
      }
    },
    cacheAspectRatio(imageId, aspectRatio) {
      this._pendingCache[imageId] = aspectRatio
      if (this._cacheFlushTimer) clearTimeout(this._cacheFlushTimer)
      this._cacheFlushTimer = setTimeout(() => this._flushAspectRatioCache(), 300)
    },
    _flushAspectRatioCache() {
      this._cacheFlushTimer = null
      if (!Object.keys(this._pendingCache).length) return
      try {
        const cache = { ...this.getAspectRatioCache(), ...this._pendingCache }
        this._pendingCache = {}
        const keys = Object.keys(cache)
        if (keys.length > 500) {
          keys.slice(0, keys.length - 500).forEach(key => delete cache[key])
        }
        localStorage.setItem('gallery-aspect-ratios', JSON.stringify(cache))
      } catch {
        // localStorage might be full or disabled
      }
    }
  }
}
</script>

<style scoped>
.gallery-grid {
  width: 100%;
  margin-bottom: 2em;
}
</style>
